import { Injectable } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { select, Store } from '@ngrx/store'
import { catchError, filter, map, switchMap, withLatestFrom } from 'rxjs/operators'
import { ErrorReportingService } from '../../shared/services/error-reporting.service'
import { FSA } from '../../shared/types'
import { AAAStore } from '../../store/root-reducer'
import { selectReferenceStrategyChain } from '../ui/ui.selectors'
import {
  fetchNearbyLandmarkRequest,
  fetchNearbyLandmarkSuccess,
  LOCATION_TYPE,
  SET_BREAKDOWN_LOCATION,
} from './location.actions'
import { AAALocation } from './location.types'
import { toLocationKey } from './map-landing.utils'
import { AwsNearbyService } from './service/aws/aws-nearby.service'

/**
 * DRRWEB-3399 `nearby` map-reference detector, store side (moved out of LocationEffects in
 * DRRWEB-3662, C1). Two effects: one turns a searched-address resolve into a lookup request, the
 * other performs the lookup only while the active arm names `nearby`.
 */
@Injectable()
export class NearbyLandmarkEffects {
  constructor(
    private actions$: Actions,
    private store$: Store<AAAStore>,
    private errorReportingService: ErrorReportingService,
    private awsNearbyService: AwsNearbyService
  ) {}

  // Fires for a location the member entered through the address search; the actual fetch
  // (fetchNearbyLandmark below) is what gates on the active arm's chain, mirroring
  // requestIpRegionOnGpsUnavailable -> resolveIpRegion in LocationEffects. Pin drops, GPS and
  // query-param resolves never run the landmark-zoom strategy, so they must not pay for the lookup.
  requestNearbyLandmarkOnBreakdownLocation = createEffect(() =>
    this.actions$.pipe(
      ofType(SET_BREAKDOWN_LOCATION.SUCCESS),
      filter(
        (action: FSA<AAALocation>) =>
          action.payload?.locationType === LOCATION_TYPE.ADDRESS_INPUT &&
          Boolean(action.payload?.latitude && action.payload?.longitude)
      ),
      map((action: FSA<AAALocation>) =>
        fetchNearbyLandmarkRequest({
          payload: {
            latitude: action.payload.latitude,
            longitude: action.payload.longitude,
          },
        })
      )
    )
  )

  /**
   * Dormant until the SearchNearby endpoint deploys (see
   * .claude/plans/DRRWEB-3399-backend-search-nearby.md) — until then every request 403s, and this
   * fails silent (no action dispatched), so an arm naming `nearby` behaves exactly like control
   * rather than widening on an incomplete answer.
   */
  fetchNearbyLandmark = createEffect(() =>
    this.actions$.pipe(
      ofType(fetchNearbyLandmarkRequest),
      withLatestFrom(this.store$.pipe(select(selectReferenceStrategyChain))),
      filter(([_, chain]) => chain.includes('nearby')),
      switchMap(([action]) =>
        this.awsNearbyService.searchNearby(action.payload).pipe(
          // Keyed to the location it answers for, so the map can tell this landing's answer from
          // a stale one — the fetch is async and the store is reset on every new location.
          map((signal) =>
            fetchNearbyLandmarkSuccess({
              payload: { ...signal, locationKey: toLocationKey(action.payload) },
            })
          ),
          catchError((error) =>
            this.errorReportingService.notifyErrorObservable(error, [])
          )
        )
      )
    )
  )
}
